import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { makeStyles, alpha } from "@material-ui/core/styles";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { toast } from "react-toastify";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import formatPhone from "../../helpers/formatPhone";
import {
  FormDialogHeader,
  FormSection,
  useFormDialogStyles,
} from "../FormDialog";

const useStyles = makeStyles((theme) => ({
  typeToggle: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
  },
  typeButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 44,
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
  },
  typeIncome: {
    color: theme.palette.type === "dark" ? "#81c784" : "#2e7d32",
    borderColor: theme.palette.type === "dark" ? "#81c784" : "#2e7d32",
    backgroundColor: alpha("#2e7d32", 0.1),
  },
  typeExpense: {
    color: theme.palette.type === "dark" ? "#e57373" : "#c62828",
    borderColor: theme.palette.type === "dark" ? "#e57373" : "#c62828",
    backgroundColor: alpha("#c62828", 0.1),
  },
  twoCols: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  valueField: {
    "& input": { fontSize: 18, fontWeight: 700 },
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
  },
  dot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 8,
  },
}));

const emptyForm = {
  type: "income",
  description: "",
  value: "",
  valueDisplay: "",
  dueDate: moment().format("YYYY-MM-DD"),
  paid: false,
  categoryId: "",
  paymentMethodId: "",
  paidAt: "",
  notes: "",
};

// converte "1.234,56" → 1234.56
const parseBRL = (str) => {
  const clean = str.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(clean);
  return isNaN(n) ? "" : n;
};

// formata dígitos puros (ex: "12345") → "123,45"
const formatBRL = (digits) => {
  const n = parseInt(digits.replace(/\D/g, ""), 10) || 0;
  return (n / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const TransactionModal = ({ open, onClose, transaction, onSave }) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [methods, setMethods] = useState([]);
  const [contact, setContact] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");

  const wasPaid = transaction?.status === "paid";

  useEffect(() => {
    if (!open) return;

    const loadOptions = async () => {
      try {
        const [catRes, methodRes] = await Promise.all([
          api.get("/transaction-categories"),
          api.get("/payment-methods"),
        ]);
        setCategories(catRes.data);
        setMethods(methodRes.data);
      } catch (err) {
        toastError(err);
      }
    };

    loadOptions();

    if (transaction) {
      const rawValue = transaction.value || 0;
      const digits = Math.round(Number(rawValue) * 100).toString();
      setForm({
        type: transaction.type || "income",
        description: transaction.description || "",
        value: rawValue,
        valueDisplay: formatBRL(digits),
        dueDate: transaction.dueDate || "",
        paid: transaction.status === "paid",
        categoryId: transaction.categoryId || "",
        paymentMethodId: transaction.paymentMethodId || "",
        paidAt: transaction.paidAt
          ? moment(transaction.paidAt).format("YYYY-MM-DDTHH:mm")
          : transaction.status === "paid"
          ? moment().format("YYYY-MM-DDTHH:mm")
          : "",
        notes: transaction.notes || "",
      });
      setContact(transaction.contact || null);
      setContactOptions(transaction.contact ? [transaction.contact] : []);
    } else {
      setForm({ ...emptyForm, dueDate: moment().format("YYYY-MM-DD") });
      setContact(null);
      setContactOptions([]);
    }
    setContactSearch("");
  }, [open, transaction]);

  // Busca de contatos com debounce.
  useEffect(() => {
    if (!open || contactSearch.trim().length < 2) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", {
          params: { searchParam: contactSearch.trim() },
        });
        setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [contactSearch, open]);

  const filteredCategories = categories.filter((c) => c.type === form.type);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "type") next.categoryId = "";
      return next;
    });
  };

  const handlePaidToggle = (e) => {
    const paid = e.target.checked;
    setForm((prev) => ({
      ...prev,
      paid,
      paidAt: paid ? moment().format("YYYY-MM-DDTHH:mm") : "",
    }));
  };

  const handleValueChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const display = formatBRL(digits || "0");
    const numeric = parseBRL(display);
    setForm((prev) => ({ ...prev, value: numeric, valueDisplay: display }));
  };

  const handleSubmit = async () => {
    if (!form.description || !form.value || Number(form.value) <= 0 || !form.dueDate) {
      toast.error("Preencha descrição, valor e vencimento.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type: form.type,
        description: form.description,
        value: Number(form.value),
        dueDate: form.dueDate,
        status: form.paid ? "paid" : "pending",
        categoryId: form.categoryId || undefined,
        paymentMethodId: form.paymentMethodId || undefined,
        contactId: contact?.id || undefined,
        paidAt: form.paid ? form.paidAt || undefined : undefined,
        notes: form.notes || undefined,
      };

      let saved;
      if (transaction?.id) {
        const { data } = await api.put(`/transactions/${transaction.id}`, payload);
        saved = data;
        toast.success("Lançamento atualizado.");
      } else {
        const { data } = await api.post("/transactions", payload);
        saved = data;
        toast.success("Lançamento criado.");
      }

      onSave(saved, !!transaction?.id);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const isEdit = !!transaction?.id;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="transaction-modal-title"
    >
      <FormDialogHeader
        id="transaction-modal-title"
        title={isEdit ? "Editar lançamento" : "Novo lançamento"}
        subtitle="Registre uma entrada ou saída do caixa."
        onClose={onClose}
        disabled={loading}
      />
      <DialogContent dividers className={dialogClasses.content}>
        <FormSection title="Tipo">
          <div className={classes.typeToggle} role="group" aria-label="Tipo de lançamento">
            <Button
              variant="outlined"
              className={`${classes.typeButton} ${form.type === "income" ? classes.typeIncome : ""}`}
              startIcon={<ArrowUpwardIcon />}
              onClick={() => handleChange("type")({ target: { value: "income" } })}
              aria-pressed={form.type === "income"}
            >
              Receita
            </Button>
            <Button
              variant="outlined"
              className={`${classes.typeButton} ${form.type === "expense" ? classes.typeExpense : ""}`}
              startIcon={<ArrowDownwardIcon />}
              onClick={() => handleChange("type")({ target: { value: "expense" } })}
              aria-pressed={form.type === "expense"}
            >
              Despesa
            </Button>
          </div>
        </FormSection>

        <FormSection title="Lançamento">
          <TextField
            label="Descrição"
            variant="outlined"
            size="small"
            fullWidth
            value={form.description}
            onChange={handleChange("description")}
            inputProps={{ maxLength: 255 }}
          />
          <div className={classes.twoCols}>
            <TextField
              label="Valor (R$)"
              variant="outlined"
              size="small"
              fullWidth
              className={classes.valueField}
              value={form.valueDisplay}
              onChange={handleValueChange}
              inputProps={{ inputMode: "numeric" }}
              placeholder="0,00"
            />
            <TextField
              label="Vencimento"
              variant="outlined"
              size="small"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.dueDate}
              onChange={handleChange("dueDate")}
            />
          </div>
          <Autocomplete
            size="small"
            options={contactOptions}
            value={contact}
            onChange={(_, v) => setContact(v)}
            onInputChange={(_, v, reason) => {
              if (reason === "input") setContactSearch(v);
            }}
            getOptionLabel={(o) => o?.name || ""}
            getOptionSelected={(o, v) => o.id === v.id}
            filterOptions={(opts) => opts}
            noOptionsText="Digite ao menos 2 letras"
            renderOption={(o) => (
              <div>
                <div>{o.name}</div>
                {o.number && (
                  <Typography variant="caption" color="textSecondary">
                    {formatPhone(o.number)}
                  </Typography>
                )}
              </div>
            )}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Contato (opcional)" />
            )}
          />
        </FormSection>

        <FormSection title="Classificação">
          <div className={classes.twoCols}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                label="Categoria"
                value={form.categoryId}
                onChange={handleChange("categoryId")}
              >
                <MenuItem value="">
                  <em>Sem categoria</em>
                </MenuItem>
                {filteredCategories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <span className={classes.dot} style={{ backgroundColor: c.color }} />
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Forma de pagamento</InputLabel>
              <Select
                label="Forma de pagamento"
                value={form.paymentMethodId}
                onChange={handleChange("paymentMethodId")}
              >
                <MenuItem value="">
                  <em>Não informado</em>
                </MenuItem>
                {methods.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </FormSection>

        <FormSection title="Pagamento">
          <div className={classes.switchRow}>
            <div>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Já foi pago
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {wasPaid
                  ? "Para desfazer um pagamento, cancele o lançamento na lista."
                  : "Ao ligar, informe quando o valor entrou ou saiu."}
              </Typography>
            </div>
            <Switch
              color="primary"
              checked={form.paid}
              onChange={handlePaidToggle}
              disabled={wasPaid}
              inputProps={{ "aria-label": "Já foi pago" }}
            />
          </div>
          {form.paid && (
            <TextField
              label="Data e hora do pagamento"
              variant="outlined"
              size="small"
              fullWidth
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.paidAt}
              onChange={handleChange("paidAt")}
            />
          )}
        </FormSection>

        <FormSection title="Observações">
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder="Opcional"
            value={form.notes}
            onChange={handleChange("notes")}
          />
        </FormSection>
      </DialogContent>
      <DialogActions className={dialogClasses.actions}>
        <Button
          onClick={onClose}
          disabled={loading}
          className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disableElevation
          disabled={loading}
          className={dialogClasses.actionButton}
        >
          {loading ? <CircularProgress size={20} /> : "Salvar lançamento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionModal;
